import * as React from 'react'

import { Box, Icon, Text, useClassNames } from '@factorialco/deprecated-design-system'

import useFilter from 'modules/core/components/Filter/base/useFilter'

import './index.styles.css'

const stylesRefs = {
  chip: 'Chip_chip',
  chipLabel: 'Chip_chipLabel',
  hideChevron: 'Chip_hideChevron',
  noBorder: 'Chip_noBorder',
  active: 'Chip_active',
  fullWidth: 'Chip_fullWidth',
}

type TextLabelChipProps = {
  label: string
}

type IconLabelChipProps = {
  icon: React.ComponentProps<typeof Icon.Medium>['icon']
}

/**
 * Props type to allow users to call chip components with either a label or an icon
 */
export type LabelChipProps = (TextLabelChipProps | IconLabelChipProps) &
  Partial<TextLabelChipProps> &
  Partial<IconLabelChipProps>

/**
 * Label props for for Chip and intermediary components.
 *
 * This is used as to avoid errors when propagating props through components,
 * as the LabelChipProps are to be used directly, not propagated from parent props.
 */
type PrivateLabelChipProps = Partial<IconLabelChipProps> & {
  label: React.ReactNode
}

type Props = PrivateLabelChipProps & {
  active: boolean
  activeLabel?: string
  hideChevron?: boolean
  noBorder?: boolean
  onClick?: () => void
  fullWidth?: boolean
}

export default function Chip({
  label,
  icon,
  active,
  activeLabel,
  hideChevron,
  noBorder,
  onClick,
  fullWidth,
}: Props) {
  const cx = useClassNames(stylesRefs)

  return (
    <button className={cx('chip', { active, hideChevron, noBorder, fullWidth })} onClick={onClick}>
      <Box className={cx('chipLabel')} flexDirection="row" alignItems="center" gap="s4">
        {icon && <Icon.Medium icon={icon} color={active ? 'viridian1000' : 'grey600'} />}
        {typeof label === 'string' ? (
          <Text.Normal
            size="200"
            weight={active ? 'semibold' : 'normal'}
            color={active ? 'viridian1200' : 'grey800'}
            whiteSpace="nowrap"
          >
            {label}
            {active && activeLabel && ':'}
          </Text.Normal>
        ) : (
          label
        )}
        {active && activeLabel && (
          <Text.Normal
            lineClamp={0}
            size="200"
            color={active ? 'viridian1200' : 'grey800'}
            ellipsis
          >
            {activeLabel}
          </Text.Normal>
        )}
      </Box>
      {!hideChevron && (
        <Icon.Small icon="chevron-down" color={active ? 'viridian1000' : 'grey600'} />
      )}
    </button>
  )
}

type TogglerChipProps = PrivateLabelChipProps & {
  active: boolean
  activeLabel?: string
  hideChevron?: boolean
  noBorder?: boolean
  onToggle?: (open: boolean) => void
  fullWidth?: boolean
}

function Toggler<Value>({
  label,
  icon,
  active,
  activeLabel,
  hideChevron,
  noBorder,
  onToggle,
  fullWidth,
}: TogglerChipProps) {
  const { open, setOpen } = useFilter<Value>()
  const handleOnClick = () => {
    const newOpen = !open

    setOpen(newOpen)
    onToggle?.(newOpen)
  }

  return (
    <Chip
      label={label}
      icon={icon}
      active={active}
      activeLabel={activeLabel}
      hideChevron={hideChevron}
      noBorder={noBorder}
      onClick={handleOnClick}
      fullWidth={fullWidth}
    />
  )
}

Chip.Toggler = Toggler
